import { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { UploadCloud, File as FileIcon, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".stl,.3mf,.obj,.step,.stp,.zip,.png,.jpg,.jpeg,.webp,.gif,.pdf";

export default function FileUpload({ onChange }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // {id, original_filename, size, uploading}
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    if (onChange) onChange(files.filter((f) => f.id).map((f) => f.id));
  }, [files, onChange]);

  const handleFiles = async (fileList) => {
    for (const file of Array.from(fileList)) {
      const tempId = `${file.name}-${Date.now()}-${Math.random()}`;
      setFiles((prev) => [...prev, { tempId, original_filename: file.name, size: file.size, uploading: true }]);
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => prev.map((f) => (f.tempId === tempId ? { ...data, uploading: false } : f)));
        toast.success(`${file.name} envoyé`);
      } catch (e) {
        setFiles((prev) => prev.filter((f) => f.tempId !== tempId));
        toast.error(e.response?.data?.detail || `Échec de l'envoi de ${file.name}`);
      }
    }
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div
        data-testid="file-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-[#E6007E] bg-[#E6007E]/10" : "border-[#0055FF] bg-[#0055FF]/10"}`}
      >
        <UploadCloud className="w-10 h-10 mx-auto text-[#0055FF]" />
        <p className="mt-3 font-semibold text-white">Glissez vos fichiers ici ou cliquez pour parcourir</p>
        <p className="mt-1 text-sm text-gray-400">STL, 3MF, OBJ, STEP, ZIP, images, PDF — max 100 Mo</p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
          data-testid="file-input"
          onChange={(e) => handleFiles(e.target.files)} />
      </div>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="file-list">
          {files.map((f, idx) => (
            <li key={f.tempId || f.id} className="flex items-center gap-3 rounded-lg bg-black/50 border border-white/10 px-3 py-2">
              <FileIcon className="w-4 h-4 text-[#E6007E] shrink-0" />
              <span className="flex-1 truncate text-sm text-gray-200">{f.original_filename}</span>
              {f.uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#0055FF]" />
                : <CheckCircle2 className="w-4 h-4 text-[#0055FF]" />}
              {!f.uploading && (
                <button onClick={() => removeFile(idx)} data-testid={`file-remove-${idx}`} className="text-gray-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
